import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Search, X, Check, Users, User, AlertCircle, MessageSquare, ChevronDown, Filter, Bell } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface MessageTemplate {
  id: string;
  name: string;
  code: string;
  category: string;
  title: string;
  content: string;
  variables: any[];
}

interface UserProfile {
  id: string;
  username: string;
  phone: string;
  role: string;
  display_name?: string;
}

interface SendFormData {
  sendType: 'single' | 'batch' | 'all';
  selectedUsers: string[];
  templateId: string;
  title: string;
  content: string;
  category: string;
  priority: 'high' | 'normal' | 'low';
  variableValues: Record<string, string>;
}

const PRIORITY_OPTIONS = [
  { value: 'high', label: '高优先级', color: 'red', icon: AlertCircle },
  { value: 'normal', label: '普通', color: 'blue', icon: Bell },
  { value: 'low', label: '低优先级', color: 'gray', icon: Bell },
];

const CATEGORY_OPTIONS = [
  { value: 'system', label: '系统通知' },
  { value: 'order', label: '订单相关' },
  { value: 'package', label: '套餐相关' },
  { value: 'permission', label: '权限相关' },
  { value: 'announcement', label: '公告' },
];

export default function SendMessage() {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<string>('all');
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  const [formData, setFormData] = useState<SendFormData>({
    sendType: 'single',
    selectedUsers: [],
    templateId: '',
    title: '',
    content: '',
    category: 'system',
    priority: 'normal',
    variableValues: {},
  });

  useEffect(() => {
    fetchTemplates();
    fetchUsers();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      setTemplates((data || []) as MessageTemplate[]);
    } catch (err) {
      
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, phone, role, display_name')
        .order('username');
      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        templateId,
        title: template.title,
        content: template.content,
        category: template.category,
        variableValues: {},
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        templateId: '',
        variableValues: {},
      }));
    }
  };

  const extractVariables = (text: string) => {
    const matches = text.match(/\{\{(\w+)\}\}/g);
    return matches ? matches.map(m => m.replace(/\{\{|\}\}/g, '')) : [];
  };

  const replaceVariables = (text: string, values: Record<string, string>) => {
    let result = text;
    Object.entries(values).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    });
    return result;
  };

  const handleSend = async () => {
    if (formData.sendType !== 'all' && formData.selectedUsers.length === 0) {
      setSendResult({ success: false, message: '请选择接收用户' });
      return;
    }
    if (!formData.title.trim() || !formData.content.trim()) {
      setSendResult({ success: false, message: '请填写标题和内容' });
      return;
    }

    setSending(true);
    setSendResult(null);

    try {
      const title = replaceVariables(formData.title, formData.variableValues);
      const content = replaceVariables(formData.content, formData.variableValues);

      let recipientIds: string[] = [];
      if (formData.sendType === 'all') {
        recipientIds = users.map(u => u.id);
      } else {
        recipientIds = formData.selectedUsers;
      }

      const messages = recipientIds.map(userId => ({
        user_id: userId,
        title,
        content,
        category: formData.category,
        priority: formData.priority,
        sender_type: 'admin',
        template_id: formData.templateId || null,
      }));

      const { error } = await supabase.from('user_messages').insert(messages);
      if (error) throw error;

      setSendResult({
        success: true,
        message: `消息发送成功！共发送给 ${recipientIds.length} 位用户`,
      });

      setFormData({
        sendType: 'single',
        selectedUsers: [],
        templateId: '',
        title: '',
        content: '',
        category: 'system',
        priority: 'normal',
        variableValues: {},
      });
    } catch (err: any) {
      setSendResult({ success: false, message: err.message || '发送失败' });
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.username?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                         u.phone?.includes(userSearchQuery) ||
                         u.display_name?.toLowerCase().includes(userSearchQuery.toLowerCase());
    const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter;
    return matchesSearch && matchesRole;
  });

  const selectedTemplate = templates.find(t => t.id === formData.templateId);
  const variables = extractVariables(formData.title + formData.content);

  const toggleUserSelection = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId],
    }));
  };

  const selectAllUsers = () => {
    const allIds = filteredUsers.map(u => u.id);
    const allSelected = allIds.every(id => formData.selectedUsers.includes(id));
    if (allSelected) {
      setFormData(prev => ({
        ...prev,
        selectedUsers: prev.selectedUsers.filter(id => !allIds.includes(id)),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedUsers: [...new Set([...prev.selectedUsers, ...allIds])],
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">发送站内信</h1>
        <p className="text-sm text-gray-500 mt-1">手动向用户发送站内消息通知</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              消息内容
            </h2>

            {sendResult && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                  sendResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}
              >
                {sendResult.success ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {sendResult.message}
              </motion.div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">发送方式</label>
                <div className="flex gap-2">
                  {[
                    { value: 'single', label: '单个用户', icon: User },
                    { value: 'batch', label: '批量选择', icon: Users },
                    { value: 'all', label: '全部用户', icon: Users },
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => setFormData(prev => ({ ...prev, sendType: type.value as any, selectedUsers: [] }))}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.sendType === type.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {formData.sendType !== 'all' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    选择用户 <span className="text-gray-400">({formData.selectedUsers.length} 人已选)</span>
                  </label>
                  <button
                    onClick={() => setShowUserSelector(true)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg hover:border-blue-400 transition-colors flex items-center justify-between"
                  >
                    <span className="text-gray-500">
                      {formData.selectedUsers.length > 0
                        ? `已选择 ${formData.selectedUsers.length} 位用户`
                        : '点击选择用户...'}
                    </span>
                    <Users className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">选择模板（可选）</label>
                <select
                  value={formData.templateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">不使用模板，自定义内容</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">消息分类</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {CATEGORY_OPTIONS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">优先级</label>
                  <div className="flex gap-2">
                    {PRIORITY_OPTIONS.map(p => (
                      <button
                        key={p.value}
                        onClick={() => setFormData(prev => ({ ...prev, priority: p.value as any }))}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.priority === p.value
                            ? `bg-${p.color}-600 text-white`
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <p.icon className="w-3.5 h-3.5" />
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  消息标题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="请输入消息标题"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  消息内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="请输入消息内容，支持使用 {{变量名}} 占位符"
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {variables.length > 0 && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-amber-900 mb-3">填写变量值</p>
                  <div className="grid grid-cols-2 gap-3">
                    {variables.map(v => (
                      <div key={v}>
                        <label className="block text-xs font-medium text-amber-700 mb-1">
                          {v}
                        </label>
                        <input
                          type="text"
                          value={formData.variableValues[v] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            variableValues: { ...prev.variableValues, [v]: e.target.value },
                          }))}
                          placeholder={`输入${v}的值`}
                          className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleSend}
                disabled={sending || (formData.sendType !== 'all' && formData.selectedUsers.length === 0)}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                {sending ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    发送中...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    发送消息
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              消息预览
            </h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  formData.priority === 'high' ? 'bg-red-100 text-red-700' :
                  formData.priority === 'normal' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {PRIORITY_OPTIONS.find(p => p.value === formData.priority)?.label}
                </span>
                <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                  {CATEGORY_OPTIONS.find(c => c.value === formData.category)?.label}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                {replaceVariables(formData.title, formData.variableValues) || '消息标题'}
              </h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {replaceVariables(formData.content, formData.variableValues) || '消息内容预览...'}
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-6 text-white">
            <h3 className="font-semibold mb-2">发送统计</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/80">目标用户</span>
                <span className="font-medium">
                  {formData.sendType === 'all' ? users.length : formData.selectedUsers.length} 人
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/80">消息分类</span>
                <span className="font-medium">
                  {CATEGORY_OPTIONS.find(c => c.value === formData.category)?.label}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/80">优先级</span>
                <span className="font-medium">
                  {PRIORITY_OPTIONS.find(p => p.value === formData.priority)?.label}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showUserSelector && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900">选择用户</h2>
                <button
                  onClick={() => setShowUserSelector(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 border-b border-gray-100 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="搜索用户名或手机号..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <select
                    value={userRoleFilter}
                    onChange={(e) => setUserRoleFilter(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">全部角色</option>
                    <option value="user">学员</option>
                    <option value="teacher">教师</option>
                    <option value="admin">管理员</option>
                  </select>
                  <button
                    onClick={selectAllUsers}
                    className="ml-auto text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {filteredUsers.every(u => formData.selectedUsers.includes(u.id)) ? '取消全选' : '全选'}
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto max-h-[400px] p-4">
                <div className="space-y-2">
                  {filteredUsers.map(user => (
                    <div
                      key={user.id}
                      onClick={() => toggleUserSelection(user.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        formData.selectedUsers.includes(user.id)
                          ? 'bg-blue-50 border border-blue-200'
                          : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        formData.selectedUsers.includes(user.id)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {formData.selectedUsers.includes(user.id) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {user.display_name || user.username}
                          <span className="ml-2 text-xs text-gray-500">({user.username})</span>
                        </p>
                        <p className="text-sm text-gray-500">{user.phone}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        user.role === 'teacher' ? 'bg-purple-100 text-purple-700' :
                        user.role === 'admin' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {user.role === 'teacher' ? '教师' : user.role === 'admin' ? '管理员' : '学员'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-6 border-t border-gray-100">
                <span className="text-sm text-gray-500">
                  已选择 {formData.selectedUsers.length} 位用户
                </span>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowUserSelector(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={() => setShowUserSelector(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    确认选择
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
